<a class="campaign-card" [routerLink]="campaign.slug | campaignCardLink: campaign.defaultView">
  <div class="campaign-card__head tw-relative">
    @if (campaign.highlightTitle || campaign.highlightBody) {
      <div
        class="tw-absolute tw-bottom-0 tw-z-[1] tw-w-full tw-bg-[#2f6a47] tw-py-1.5 tw-pl-7 tw-text-xs tw-leading-none tw-text-white"
      >
        <div class="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-pr-[110px]">
          @if (campaign.highlightTitle) {
            <span>
              {{ campaign.highlightTitle }}
              @if (campaign.highlightBody) {
                <span class="tw-px-1">-</span>
              }
            </span>
          }
          @if (campaign.highlightBody) {
            <span>{{ campaign.highlightBody }}</span>
          }
        </div>
      </div>
    }
    <div class="campaign-card__top-badge">
      @if (campaign?.isPrivate && !campaign?.isInvited) {
        <div class="status-badge badge--icon tw-bg-private-light tw-text-private">
          <p-icon class="badge-icon" name="lock" [iconClass]="'tw-w-4 tw-h-4'"></p-icon>
          {{ 'common.inviteOnly' | translate }}
        </div>
      }
      @if (campaign.showCustomHighlight) {
        <div class="status-badge yellow-tag">
          <img
            src="assets/img/icons/common/fluent_sparkle-28-filled.png"
            alt="Custom Highlight"
            width="15"
            height="13"
          />
          {{ campaign.customHighlightMessage }}
        </div>
      }
      @if (campaign?.isPrivate && campaign?.isInvited) {
        <div
          class="status-badge badge--icon badge--highlight tw-bg-private-lightest tw-text-private"
        >
          <p-icon class="badge-icon" name="unlock" [iconClass]="'tw-w-4 tw-h-4'"></p-icon>
          {{ 'common.invited' | translate }}
        </div>
      }
      @if (campaign.isAngelTaxIncentive) {
        <div class="status-badge success-background">ATI</div>
      }
      @if (campaign.isShariah) {
        <div class="status-badge tw-bg-[#004e5f] tw-text-white tw-text-opacity-80">
          <i class="status-badge-icon pitchin-icon-shariah tw-mr-0.5 tw-align-top"></i>
          {{ 'company.shariahCompliant' | translate }}
        </div>
      }
    </div>
    <div class="campaign-card__banner tw-relative">
      <img
        [src]="campaign.company?.companyBannerUrl || defaultBanner"
        [alt]="campaign.company?.brandName"
      />
    </div>
  </div>

  <div class="campaign-card__body">
    <div class="tw-flex tw-gap-2">
      <div class="campaign-card__title p-text-emphasis tw-line-clamp-1">
        {{ campaign.company?.brandName }}
      </div>
      <div class="campaign-card__logo">
        <img
          class="!tw-object-cover"
          [src]="campaign.company?.companyLogoUrl || defaultLogo"
          [alt]="campaign.company?.brandName"
        />
      </div>
    </div>
    <div class="campaign-card__desc p-text-body tw-line-clamp-3">
      {{ campaign.company?.tagline }}
    </div>
    <app-tag-list
      [tags]="campaign.company?.sectors"
      [excludes]="['Shariah']"
      [partnershipBacked]="campaign.partnershipBacked"
      [campaignImpacts]="campaign.campaignImpacts"
    />
  </div>

  <div class="campaign-card__footer">
    <div class="tw-flex tw-flex-col">
      <!-- Raised amount -->
      @if (campaign.campaignStatus != CampaignStatus.PreLive) {
        <div>
          <div class="campaign-card__stat-label p-text-tag">
            {{ 'campaign.raised' | translate }}
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="campaign-card__numbers tw-mr-auto">
              {{ campaign.bankedInAmount | numberSuffix }}
              ({{ calculateRaisedPercentange() }}%)
            </div>
            <!-- Show days left -->
            @if (campaign.campaignStatus != CampaignStatus.Cooling && CampaignStatus.PreLive) {
              <div class="campaign-card__dayleft tw-whitespace-nowrap">
                @if (
                  campaign.campaignStatus == CampaignStatus.Live &&
                  !campaign.isEnded &&
                  calculateDayLeft(campaign.endAt) >= 2
                ) {
                  <span>
                    {{ campaign.endAt | amDifference: today : 'days' : true | number: '1.0-0' }}
                    {{
                      'campaign.daysLeft_plural'
                        | translate: { count: calculateDayLeft(campaign.endAt) }
                    }}
                    to invest
                  </span>
                }
                @if (campaign.campaignStatus == CampaignStatus.Live && campaign.isTomorrow) {
                  <span> {{ 'campaign.endTomorrow' | translate }} to invest </span>
                }
                @if (campaign.campaignStatus == CampaignStatus.Live && campaign.isToday) {
                  <span> {{ 'campaign.endToday' | translate }} to invest </span>
                }
                @if (
                  (campaign.campaignStatus != CampaignStatus.Cooling && campaign.isEnded) ||
                  campaign.campaignStatus == CampaignStatus.ShareAllotment
                ) {
                  <span class="tw-text-secondary-light">
                    {{ 'campaign.funded' | translate }}
                  </span>
                }
                @if (campaign.campaignStatus == CampaignStatus.Fail) {
                  <span>
                    {{ 'campaign.Fail' | translate }}
                  </span>
                }
              </div>
            }
            <!-- Cooling off label -->
            @if (campaign.campaignStatus == CampaignStatus.Cooling) {
              <div class="campaign-card__status tw-whitespace-nowrap tw-bg-info-light">
                {{ 'campaign.' + CampaignStatus[campaign.campaignStatus] | translate }}
              </div>
            }
          </div>
        </div>
      }

      @if (campaign.campaignStatus == CampaignStatus.PreLive) {
        <div class="campaign-card__status tw-bg-warning-light">
          {{ 'campaign.launchingSoon' | translate }}
        </div>
      }

      <!-- Progress bar -->
      @if (
        campaign.campaignStatus == CampaignStatus.Live ||
        campaign.campaignStatus == CampaignStatus.Cooling
      ) {
        <div class="tw-my-2.5">
          <mat-progress-bar
            aria-label="progressbar"
            mode="determinate"
            height="10"
            [color]="campaign.raisedPercentage > 100 ? 'success' : ''"
            [value]="calculateProgress(campaign.raisedPercentage)"
          >
          </mat-progress-bar>
        </div>
      } @else {
        <div class="tw-my-3.5">
          <hr class="tw-my-0 tw-border-divider" />
        </div>
      }

      <div
        class="campaign-card__stats tw-grid tw-divide-x tw-divide-y-0 tw-divide-solid tw-divide-divider"
        [class.tw-grid-cols-3]="campaign.campaignStatus != CampaignStatus.PreLive"
        [class.tw-grid-cols-2]="campaign.campaignStatus == CampaignStatus.PreLive"
        [class.!tw-grid-cols-2]="campaign?.shareType === ShareType.Ulp"
      >
        <!-- Valuation -->
        @if (campaign.slug != 'onigem-democratizing-ruby-investment') {
          <div class="campaign-card__stat">
            @if (campaign?.shareType !== ShareType.Ulp) {
              <div class="campaign-card__stat">
                <div class="campaign-card__stat-label p-text-tag">
                  {{ 'campaign.valuation' | translate }}
                </div>
                <div class="campaign-card__numbers tw-text-sm">
                  {{
                    campaign.valuationAmount
                      | numberSuffix
                        : {
                            isSuffix: true,
                            numberFormat: campaign.valuationAmount >= 1000000 ? '1.1-1' : '1.0-1',
                          }
                  }}
                </div>
              </div>
            }
          </div>
        }

        <!-- Target -->
        <div class="campaign-card__stat">
          <div class="campaign-card__stat-label p-text-tag">
            {{ 'campaign.minTarget' | translate }}
          </div>
          <div class="campaign-card__numbers tw-text-sm">
            {{
              campaign.minimumTargetAmount
                | numberSuffix
                  : {
                      isSuffix: true,
                      numberFormat: campaign.minimumTargetAmount >= 1000000 ? '1.1-1' : '1.0-1',
                    }
            }}
          </div>
        </div>

        <!-- Investors -->
        @if (campaign.campaignStatus != CampaignStatus.PreLive) {
          <div class="campaign-card__stat">
            <div class="campaign-card__stat-label p-text-tag">
              {{ 'campaign.investors' | translate }}
            </div>
            <div class="campaign-card__numbers tw-text-sm">
              {{ campaign.investorCount | number }}
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</a>
